import React, { useState, useEffect } from 'react';
import { errorBoundary, withInject } from '@piwikpro/platform';
import { Notifications } from '@piwikpro/notification-crate';
import {
  Button,
  Tag,
  HelpLink,
  Text,
  Card,
  Stack,
  Spinner,
  withTranslation,
} from '@piwikpro/ui-components';
import { AuthSettings } from '@piwikpro/auth-crate';
import { UserAuthSettingsState } from '@piwikpro/auth-crate/reducers/auth';
import TwoFactorConfigureOverlay from './TwoFactorConfigureOverlay';
import TwoFactorDisable from './TwoFactorDisable';
import { FailMessage } from '../components/FailMessage';

export interface ITwoFactorAuthProps {
  authService: AuthSettings
  isLoading: UserAuthSettingsState['isLoading']
  failed: UserAuthSettingsState['failed']
  twoFactorAuth: UserAuthSettingsState['twoFactorAuth']
  notifications: Notifications
  userId: string
  t: (key: string) => string
}

export const TwoFactorAuth: React.FC<ITwoFactorAuthProps> = ({
  authService,
  isLoading,
  failed,
  twoFactorAuth,
  notifications,
  userId,
  t,
}) => {
  const [configViewVisible, setConfigViewVisible] = useState(false);
  const [disableViewVisible, setDisableViewVisible] = useState(false);
  const twoAuthEnabled = twoFactorAuth.enabled;
  const twoAuthEnforced = twoFactorAuth.enforced;

  const fetchConfigData = async () => {
    try {
      await authService.getSettingsForUser(userId);
    // eslint-disable-next-line no-empty
    } catch {}
  };
  const handleActionButton = () => {
    if (twoAuthEnabled) {
      setDisableViewVisible(true);
    } else {
      setConfigViewVisible(true);
    }
  };
  const handleClose = (
    { setupCompleted } = { setupCompleted: false },
  ) => {
    if (setupCompleted) {
      notifications.push({
        text: twoAuthEnforced ? 'profile:security.2fa.notifications.reset.success' : 'profile:security.2fa.notifications.turned-on.success',
        type: 'success',
      });
    }
    setConfigViewVisible(false);
    setDisableViewVisible(false);
    fetchConfigData();
  };

  useEffect(() => {
    fetchConfigData();
  }, []);

  if (isLoading) return <Spinner />;

  if (failed) return <FailMessage />;

  return (
    <>
      <Stack vertical horizontalAlignment="middle">
        <Stack.Item fill>
          <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
            {t(twoAuthEnforced
              ? 'security.2fa.section.settings.reset.header'
              : 'security.2fa.section.settings.set.header')
            }
          </Text>
        </Stack.Item>
        <Stack.Item previousSpacing="narrow">
          <Text as="p">
            {t(twoAuthEnforced
              ? 'security.2fa.section.settings.reset.description'
              : 'security.2fa.section.settings.set.description')
            }
          </Text>
        </Stack.Item>
        <Stack.Item fill>
          {
            twoAuthEnforced
              ? (
                <Button
                  text={t('security.2fa.section.settings.reset.buttonText')}
                  onClick={() => setConfigViewVisible(true)}
                />
              ) : (
                <Card>
                  <Card.Section padding="narrow" subdued>
                    <Stack verticalAlignment="center">
                      <Stack.Item fill>
                        <Stack verticalAlignment="center" spacing="narrow">
                          <Stack.Item>
                            <Text as="p" weight={600} dataId="Label">{t('security.2fa.section.settings.set.status.header')}</Text>
                          </Stack.Item>
                          <Stack.Item>
                            <Tag
                              color={twoAuthEnabled ? 'green' : 'gray'}
                            >
                              {t(twoAuthEnabled ? 'security.2fa.section.settings.set.status.turnedOn' : 'security.2fa.section.settings.set.status.turnedOff')}
                            </Tag>
                          </Stack.Item>
                        </Stack>
                      </Stack.Item>
                      <Stack.Item>
                        <Button
                          appearance="simple"
                          text={t(twoFactorAuth.enabled ? 'security.2fa.section.settings.set.button.turnOff' : 'security.2fa.section.settings.set.button.turnOn')}
                          onClick={handleActionButton}
                        />
                      </Stack.Item>
                    </Stack>
                  </Card.Section>
                </Card>
              )}
        </Stack.Item>
        <Stack.Item>
          <HelpLink href="https://help.piwik.pro/support/account/two-factor-authentication/">
            Set up two-factor authentication (2FA)
          </HelpLink>
        </Stack.Item>
      </Stack>
      {/* @ts-ignore:next-line */}
      {configViewVisible && <TwoFactorConfigureOverlay onClose={handleClose} />}
      {disableViewVisible && <TwoFactorDisable onClose={handleClose} />}
    </>
  );
};

export default withInject({
  authService: 'AuthCrate.authSettings',
  notifications: 'NotificationCrate.notifications',
})(withTranslation(['profile', 'platform'])(errorBoundary(TwoFactorAuth)));
